import React, { Fragment, useEffect, useState } from 'react'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { ClearAdornment } from '../../UI/Adornments/ClearAdornment'
import { GenericInputAction } from '../../UI/GenericInputAction/GenericInputAction'
import { patchResolveAlert, validatePosition } from '../config/api'
import { eFlow } from '../config/configurations'
import { StatusColorInputBase } from '../../UI/GenericInputAction/types'
import { eTypePosition } from '../../../enums/eTypePosition'
import { PositionInfo } from '../../../interfaces/Position/Position'
import { utcToLocal } from '../../../utils/dateHelper'
import { useExpirationIndicator } from '../../../hooks/useExpirationStatus'

interface AddAlertModalProps {
  positionName: string
  positionAlertQuantity: number
  sku: string
  positionAlertDetailId: number
  flowId: number
  externalStorage: boolean
  typePositionId: number
  batchId?: number | null
  batchNumber?: string | null
  expirationDate?: Date | null
  expirationStatus?: string | null
  closeModal: (message: string, success: boolean) => void
}

export const AddAlertModal: React.FC<AddAlertModalProps> = ({
  positionName,
  positionAlertQuantity,
  sku,
  positionAlertDetailId,
  flowId,
  externalStorage,
  typePositionId,
  batchId,
  batchNumber,
  expirationDate,
  expirationStatus,
  closeModal
}) => {
  const { callApi, state } = useApi()
  const { callApi: reqValidatePositon, state: stateValidatePosition } =
    useApi<PositionInfo>()

  const [skuEan, setSku] = useState('')
  const [skuStatus, setSkuStatus] = useState<{
    status: StatusColorInputBase
    message: string
  }>({ status: 'default', message: '' })

  const [qtyStatus, setQtyStatus] = useState<{
    status: StatusColorInputBase
    message: string
  }>({ status: 'default', message: '' })

  const [quantity, setQuantity] = useState<number | undefined>()
  const [detinationPosition, setDetinationPosition] = useState<string>()
  const [destinationError, setDestinationError] = useState<string>('')

  let expirationIndicator: string = ''
  if (batchId) {
    expirationIndicator = useExpirationIndicator(expirationStatus!)
  }

  const requestValidateSKU = () => {
    if (sku !== skuEan) {
      setSkuStatus({
        status: 'error',
        message: 'SKU no está asociado a la alerta de inventario'
      })
      return
    }

    setSkuStatus({
      status: 'success',
      message: ''
    })
    // Setear solo si es menor que la cantidad máxima
    // borrar el sku
    setQuantity((prevQuantity) => (prevQuantity || 0) + 1)
    setSku('')
  }

  const requestValidateDetination = () => {
    reqValidatePositon(
      validatePosition(detinationPosition || '', eTypePosition.Reserva)
    )
      .then(() => {
        setDestinationError('')
        document.getElementById('skuEan')?.focus()
      })
      .catch((error) => {
        setDestinationError(error)
      })
  }

  // se ejecuta solo si el flowId es Almacenamiento Externo = 8
  const requestValidateQuantity = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (quantity !== positionAlertQuantity) {
        setQtyStatus({
          status: 'error',
          message: 'Cantidad ingresada incorrecta'
        })
        return
      }

      setSkuStatus({
        status: 'success',
        message: ''
      })
      requestRevolveAlert()
    }
  }

  const requestRevolveAlert = () => {
    callApi(
      patchResolveAlert(
        positionAlertDetailId,
        quantity || 0,
        externalStorage,
        stateValidatePosition.data?.id || 0
      )
    )
      .then(() => closeModal('Incidencia resuelta correctamente', true))
      .catch((error) => closeModal(error, false))
  }

  useEffect(() => {
    if (
      positionAlertQuantity === quantity &&
      flowId !== eFlow.AlmacenamientoExterno
    ) {
      requestRevolveAlert()
    }
  }, [quantity])

  useEffect(() => {
    if (flowId === eFlow.AlmacenamientoExterno) {
      setSku(sku)
    }
  }, [])

  const adornment = (
    <ClearAdornment
      onClick={() => setSku('')}
      disabled={state.status === Status.Success}
    />
  )

  const getStatusString = () => {
    if (state.status === Status.Error) {
      return 'error'
    }

    if (state.status === Status.Success) {
      return 'success'
    }

    return 'default'
  }

  // valida si es de almacenamiento externo, y debe ir a Reserva
  const withDestinationPotitionRequired = () => {
    if (externalStorage && typePositionId === eTypePosition.DraftReserva)
      return true

    return false
  }

  const validateDisableSku = () => {
    if (
      state.status === Status.Loading ||
      flowId === eFlow.AlmacenamientoExterno ||
      withDestinationPotitionRequired()
    ) {
      if (withDestinationPotitionRequired() && stateValidatePosition.data) {
        return false
      }
      return true
    }

    return false
  }

  return (
    <Fragment>
      <GenericInputAction
        value={positionName}
        onChange={undefined}
        adornment={<ClearAdornment onClick={() => {}} disabled={false} />}
        status={'success'}
        placeholder={''}
        name={'PositionCode'}
        disabled
      />
      {withDestinationPotitionRequired() && (
        <GenericInputAction
          value={detinationPosition || ''}
          onChange={(e) => setDetinationPosition(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') requestValidateDetination()
          }}
          adornment={<ClearAdornment onClick={() => {}} disabled={false} />}
          status={'success'}
          placeholder={'Ingrese Posición de Destino (Reserva)'}
          name={'PositionDestination'}
          autoFocus
          error={destinationError.length > 0}
          errorText={destinationError}
        />
      )}
      {batchId && (
        <GenericInputAction
          value={batchNumber || utcToLocal(expirationDate!, 'dd-MM-yyyy')}
          onChange={undefined}
          adornment={<ClearAdornment onClick={() => {}} disabled={false} />}
          status={'success'}
          placeholder={''}
          name={'Batch'}
          disabled={true}
          backgroundColor={expirationIndicator}
        />
      )}
      <GenericInputAction
        name='skuEan'
        id='skuEan'
        placeholder='Escanea SKU/EAN'
        disabled={validateDisableSku()}
        onChange={(e) => setSku(e.target.value)}
        value={skuEan}
        adornment={adornment}
        status={getStatusString()}
        error={skuStatus.status === 'error'}
        errorText={skuStatus.message}
        onKeyPress={(e) => {
          if (e.key === 'Enter') requestValidateSKU()
        }}
        autoFocus={!withDestinationPotitionRequired()}
      />
      <GenericInputAction
        type={'number'}
        value={quantity?.toString() || ''}
        onChange={(e) => setQuantity(parseInt(e.target.value))}
        adornment={<ClearAdornment onClick={() => {}} disabled={false} />}
        status={'default'}
        placeholder={`Ingrese cantidad (${
          positionAlertQuantity - (quantity || 0)
        })`}
        name={'quantity'}
        id={'quantity'}
        disabled={flowId !== eFlow.AlmacenamientoExterno}
        onKeyPress={(e) => {
          if (flowId === eFlow.AlmacenamientoExterno) requestValidateQuantity(e)
        }}
        error={
          flowId === eFlow.AlmacenamientoExterno && qtyStatus.status === 'error'
        }
        errorText={qtyStatus.message}
        autoFocus={flowId === eFlow.AlmacenamientoExterno}
      />
    </Fragment>
  )
}
